'use client';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import Image from 'next/image';
import { Card, CardContent } from '@/components/ui/card';
import { components } from '@/types/strapi';
import { ContactForm } from '../../ContactForm';

const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.2,
			delayChildren: 0.3,
		},
	},
};
const itemVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.5,
			ease: 'easeOut',
		},
	},
};

const cardVariants = {
	hidden: { opacity: 0, scale: 0.95 },
	visible: {
		opacity: 1,
		scale: 1,
		transition: {
			duration: 0.6,
			ease: 'easeOut',
		},
	},
};

const fadeInUpVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.6,
			ease: 'easeOut',
		},
	},
};

export const ContactSection = ({
	heading,
	sub_heading,
	trust_seal,
	contact_features,
	contact_information,
	isFirst,
}: components['schemas']['DynamicZoneContactComponent'] & {
	isFirst: boolean;
}) => {
	const SubsectionTitle = isFirst ? motion.h2 : motion.h3;

	return (
		<motion.section
			initial={false}
			whileInView="visible"
			viewport={{ once: true }}
			variants={containerVariants}
			className="contact-section relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20">
			<noscript>
				<style>{`
					.contact-section[style*='opacity'],
					.contact-section [style*='opacity'],
					.contact-section [style*='scale'],
					.contact-section [style*='translateX'],
					.contact-section [style*='translateY'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			{/* Background decoration */}
			<motion.div
				data-testid="motion-section"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 1 }}
				className="bg-grid-gray-200/25 absolute inset-0 bg-[size:20px_20px]"
			/>
			<div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/50 to-transparent" />

			<div className="container relative mx-auto px-4">
				<motion.div
					variants={fadeInUpVariants}
					className="mx-auto mb-12 max-w-3xl text-center">
					{isFirst && heading && (
						<motion.h1
							variants={itemVariants}
							className="mb-4 text-4xl font-bold tracking-tight">
							{heading}
						</motion.h1>
					)}
					{!isFirst && heading && (
						<motion.h2
							variants={itemVariants}
							className="mb-4 text-4xl font-bold tracking-tight">
							{heading}
						</motion.h2>
					)}
					{sub_heading && (
						<motion.p
							variants={itemVariants}
							className="text-xl text-gray-600">
							{sub_heading}
						</motion.p>
					)}
				</motion.div>

				<div
					data-testid="motion-section"
					className="mx-auto grid max-w-5xl items-start gap-12 md:grid-cols-2">
					<motion.div
						initial={false}
						variants={cardVariants}
						whileHover={{ scale: 1.02 }}
						transition={{ duration: 0.3 }}>
						<div className="relative">
							<Card className="overflow-hidden bg-white/95 shadow-2xl">
								<CardContent className="p-0">
									<ContactForm />
								</CardContent>
							</Card>
						</div>
					</motion.div>

					<motion.div
						variants={containerVariants}
						className="space-y-6">
						<div
							data-testid="motion-section"
							className="overflow-hidden border-none bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white">
							{contact_features && contact_features.title && (
								<SubsectionTitle
									variants={itemVariants}
									className="mb-4 text-2xl font-semibold">
									{contact_features?.title}
								</SubsectionTitle>
							)}
							{!contact_features && (
								<motion.ul
									variants={containerVariants}
									className="space-y-3">
									{[
										'Industry-leading delivery rates',
										'24/7 customer support',
										'Scalable solutions for businesses of all sizes',
										'Advanced analytics and reporting',
										'Seamless integration with your existing systems',
									].map((item, index) => (
										<motion.li
											key={index}
											variants={itemVariants}
											className="flex items-center gap-3"
											whileHover={{ x: 5 }}
											transition={{
												duration: 0.2,
											}}>
											<CheckCircle className="h-5 w-5 flex-shrink-0" />
											<span>{item}</span>
										</motion.li>
									))}
								</motion.ul>
							)}
							{contact_features?.feature_list && (
								<motion.ul
									variants={containerVariants}
									className="space-y-3">
									{contact_features.feature_list?.map(
										(item, index) => (
											<motion.li
												key={index}
												variants={itemVariants}
												className="flex items-center gap-3">
												<CheckCircle className="h-5 w-5 flex-shrink-0" />
												{item.text && (
													<span>{item.text}</span>
												)}
											</motion.li>
										),
									)}
								</motion.ul>
							)}
						</div>

						<motion.div
							data-testid="motion-section"
							variants={cardVariants}
							whileHover={{ scale: 1.02 }}
							transition={{ duration: 0.3 }}>
							<div className="overflow-hidden border-none p-6 shadow-lg">
								{contact_information?.title && (
									<SubsectionTitle
										variants={itemVariants}
										className="mb-4 text-2xl font-semibold">
										{contact_information.title ||
											'Contact Information'}
									</SubsectionTitle>
								)}

								<motion.ul
									variants={containerVariants}
									className="space-y-4">
									{contact_information?.feature_list?.map(
										(item, index) => (
											<motion.li
												key={index}
												variants={itemVariants}>
												{item.icon?.url &&
													item.text && (
														<motion.a
															href={
																item.href || '#'
															}
															className="flex items-center gap-3 text-gray-600 transition-colors hover:text-blue-600"
															whileHover={{
																x: 5,
															}}
															transition={{
																duration: 0.2,
															}}>
															<Image
																src={
																	item.icon
																		?.url
																}
																alt="abc"
																width={21}
																height={21}
																className="text-blue-500"
															/>
															<span>
																{item.text}
															</span>
														</motion.a>
													)}
											</motion.li>
										),
									)}
								</motion.ul>
							</div>
						</motion.div>
						{trust_seal && trust_seal.url && (
							<motion.div
								data-testid="motion-section"
								variants={cardVariants}
								whileHover={{ scale: 1.02 }}
								transition={{ duration: 0.3 }}
								className="relative overflow-hidden rounded-lg shadow-lg">
								<Image
									src={trust_seal.url}
									alt="Mtalkz Seal of Trust"
									layout="responsive"
									width={1024}
									height={297}
									className="rounded-lg"
								/>
							</motion.div>
						)}
					</motion.div>
				</div>
			</div>
		</motion.section>
	);
};
